import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Settings, Tv2, Plus, ChevronDown } from 'lucide-react';
import { Channel } from '../types/channel';
import { Video } from '../types/video';
import { Playlist } from '../types/playlist';
import { useChannel } from '../hooks/useChannel';
import { usePlaylists } from '../hooks/usePlaylists';
import { apiClient } from '../lib/apiClient';
import ChannelHeader from './ChannelHeader';
import ChannelSettingsPanel from './ChannelSettingsPanel';
import VideoCard from './VideoCard';
import PlaylistCard from './PlaylistCard';
import PlaylistPage from './PlaylistPage';
import { ConfirmDialog, TextInputDialog } from './ConfirmDialog';
import { toast } from '../lib/toast';
import { useReadOnly } from '../hooks/useArchiveAccess';

interface ChannelPageProps {
  /** If provided, renders the channel page for an external handle (public view). */
  handle?: string;
  /** Tenant that owns the external channel (it may be another tenant's channel). */
  channelSlug?: string;
  onEdit?: (video: Video) => void;
  myEmail?: string | null;
  /** Called when the user clicks "back" in external channel view. */
  onBack?: () => void;
}

const EmptyState: React.FC<{ onSetup?: () => void }> = ({ onSetup }) => {
  const { t } = useTranslation();
  return (
  <div
    style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      flex: 1, gap: 12, padding: 40, textAlign: 'center',
    }}
  >
    <Tv2 size={48} style={{ color: 'var(--text-secondary, #9ca3af)', opacity: 0.5 }} aria-hidden />
    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: 'var(--card-title-color, var(--text-primary, #111))' }}>
      {t('channel.no_channel_title')}
    </h3>
    <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary, #6b7280)', maxWidth: 320 }}>
      {t('channel.no_channel_description')}
    </p>
    {onSetup && <button
      onClick={onSetup}
      style={{
        padding: '9px 20px', borderRadius: 6, border: 'none',
        background: 'var(--theme-primary, #1976d2)', color: 'var(--button-primary-foreground, #fff)',
        fontWeight: 600, fontSize: 14, cursor: 'pointer',
      }}
    >
      {t('channel.create_channel_button')}
    </button>}
  </div>
);
};

const NoVideos: React.FC = () => {
  const { t } = useTranslation();
  return (
  <div
    style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      flex: 1, gap: 8, padding: 40, textAlign: 'center',
    }}
  >
    <Tv2 size={36} style={{ color: 'var(--text-secondary, #9ca3af)', opacity: 0.4 }} aria-hidden />
    <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary, #6b7280)' }}>
      {t('channel.no_videos')}
    </p>
  </div>
);
};

type ChannelTab = 'videos' | 'playlists';

const ChannelPage: React.FC<ChannelPageProps> = ({ handle, channelSlug, onEdit, myEmail, onBack }) => {
  const { t } = useTranslation();
  const {
    channels: myChannels,
    activeChannel: myChannel,
    activeChannelId,
    setActiveChannelId,
    maxChannels,
    canCreateMore,
    loading: myLoading,
  } = useChannel();

  // Local override so onChannelReady can update the header without a full refresh flash.
  const [myChannelOverride, setMyChannelOverride] = useState<Channel | null>(null);
  const [externalChannel, setExternalChannel] = useState<Channel | null>(null);
  const [externalLoading, setExternalLoading] = useState(false);
  // Set when the external channel can't be opened (no access, or it no longer exists).
  const [externalError, setExternalError] = useState<string | null>(null);
  const [channelVideos, setChannelVideos] = useState<Video[]>([]);
  const [videosLoading, setVideosLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showChannelPicker, setShowChannelPicker] = useState(false);
  const [activeTab, setActiveTab] = useState<ChannelTab>('videos');
  const [openPlaylist, setOpenPlaylist] = useState<Playlist | null>(null);
  // Playlist actions wait for confirmation in an in-app dialog.
  const [playlistAction, setPlaylistAction] = useState<
    { kind: 'visibility' | 'delete'; playlist: Playlist } | null
  >(null);
  const [showNewPlaylist, setShowNewPlaylist] = useState(false);
  // Admin Center Archive Mode (Grace period): watching only, nothing to create or change.
  const readOnly = useReadOnly();

  // Determine which channel to display
  const isOwnerView = !handle;
  const resolvedMyChannel = myChannelOverride ?? myChannel;
  const channel: Channel | null = isOwnerView ? resolvedMyChannel : externalChannel;
  const loading = isOwnerView ? myLoading : externalLoading;

  // Every channel request goes to the channel's own tenant.
  const resolvedChannelSlug = channel?.slug || channelSlug;
  const { playlists, createPlaylist, deletePlaylist, addVideo, removeVideo, setVisibility } =
    usePlaylists(channel?.handle, resolvedChannelSlug);

  // Fetch external channel if handle is provided
  useEffect(() => {
    if (!handle) return;
    let cancelled = false;
    setExternalLoading(true);
    setExternalError(null);
    // Drop the previously shown channel, so a failed load shows the error instead of it.
    setExternalChannel(null);
    setChannelVideos([]);
    apiClient.getChannelByHandle(handle, channelSlug).then((result) => {
      if (cancelled) return;
      if (result.data) setExternalChannel(result.data);
      else setExternalError(t('channel.access_restricted', "This channel isn't available, or you don't have access to it."));
      setExternalLoading(false);
    });
    return () => { cancelled = true; };
  }, [handle, channelSlug, t]);

  // Fetch videos that belong to this channel via the dedicated endpoint.
  // Same visibility rules as the channel, so this also works for public channels opened by link.
  const channelId = channel?.id;
  useEffect(() => {
    if (!channelId) return;
    let cancelled = false;
    setVideosLoading(true);
    apiClient.listChannelVideos(channelId, resolvedChannelSlug).then((result) => {
      if (cancelled) return;
      setChannelVideos(result.data ?? []);
      setVideosLoading(false);
    });
    return () => { cancelled = true; };
  }, [channelId, resolvedChannelSlug]);

  if (loading) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: 'var(--text-secondary, #6b7280)', fontSize: 13 }}>{t('channel.loading')}</div>
      </div>
    );
  }

  if (!isOwnerView && !channel && externalError) {
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
        <div style={{ color: 'var(--text-secondary, #6b7280)', fontSize: 14 }}>{externalError}</div>
        {onBack && (
          <button
            onClick={onBack}
            style={{ fontSize: 13, color: 'var(--primary-text, #1976d2)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500 }}
          >
            ← {t('channel_directory', 'Channels')}
          </button>
        )}
      </div>
    );
  }

  // Owner with no active channel selected and settings not open → prompt to create
  if (isOwnerView && !channel && !showSettings) {
    return (
      <div style={{ display: 'flex', flex: 1, flexDirection: 'column', height: '100%' }}>
        {/* Show switcher even in empty state (user may have other channels but selected null) */}
        {myChannels.length > 0 && (
          <div style={{ padding: '8px 16px', borderBottom: '1px solid var(--theme-border, #e5e7eb)' }}>
            <button
              onClick={() => setActiveChannelId(myChannels[0].id)}
              style={{
                fontSize: 13, color: 'var(--primary-text, #1976d2)',
                background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500,
              }}
            >
              ← {t('channel.back_to_channels')}
            </button>
          </div>
        )}
        <EmptyState onSetup={readOnly ? undefined : () => setShowSettings(true)} />
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex', flex: 1, height: '100%',
        background: 'var(--bg-primary, #f9fafb)',
        overflow: 'hidden',
      }}
    >
      {/* Main channel content */}
      <div
        style={{
          flex: 1, display: 'flex', flexDirection: 'column',
          overflow: 'hidden', minWidth: 0,
        }}
      >
        {/* ── Channel switcher (owner with multiple channels) ── */}
        {isOwnerView && !openPlaylist && myChannels.length > 0 && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 16px',
            borderBottom: '1px solid var(--theme-border, #e5e7eb)',
            background: 'var(--card-bg, var(--theme-card, #fff))',
            position: 'relative',
          }}>
            {/* Channel picker button */}
            <button
              onClick={() => setShowChannelPicker((v) => !v)}
              aria-haspopup="listbox"
              aria-expanded={showChannelPicker}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '5px 10px', borderRadius: 6, cursor: 'pointer',
                fontSize: 13, fontWeight: 600,
                background: 'var(--surface-hover, #f3f4f6)',
                color: 'var(--text-primary, #111)',
                border: '1px solid var(--theme-border, #e5e7eb)',
                maxWidth: 220, overflow: 'hidden',
              }}
            >
              <Tv2 size={14} aria-hidden style={{ flexShrink: 0 }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {resolvedMyChannel?.displayName ?? t('channel.select_channel')}
              </span>
              <ChevronDown size={13} aria-hidden style={{ flexShrink: 0, opacity: 0.6 }} />
            </button>

            {/* Dropdown */}
            {showChannelPicker && (
              <div
                style={{
                  position: 'absolute', top: '100%', left: 16, zIndex: 50,
                  background: 'var(--card-bg, var(--theme-card, #fff))',
                  border: '1px solid var(--theme-border, #e5e7eb)',
                  borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                  minWidth: 220, overflow: 'hidden',
                }}
                role="listbox"
              >
                {myChannels.map((ch) => (
                  <button
                    key={ch.id}
                    role="option"
                    aria-selected={ch.id === activeChannelId}
                    onClick={() => {
                      setActiveChannelId(ch.id);
                      setMyChannelOverride(null);
                      setShowChannelPicker(false);
                      setShowSettings(false);
                    }}
                    style={{
                      width: '100%', textAlign: 'left',
                      padding: '9px 14px', border: 'none', cursor: 'pointer',
                      fontSize: 13,
                      background: ch.id === activeChannelId
                        ? 'color-mix(in srgb, var(--theme-primary, #1976d2) 8%, transparent)'
                        : 'transparent',
                      color: ch.id === activeChannelId
                        ? 'var(--theme-primary, #1976d2)'
                        : 'var(--text-primary, #111)',
                      fontWeight: ch.id === activeChannelId ? 600 : 400,
                      display: 'flex', alignItems: 'center', gap: 8,
                    }}
                  >
                    <Tv2 size={13} aria-hidden />
                    {ch.displayName}
                  </button>
                ))}

                {/* Create new channel option */}
                {canCreateMore && !readOnly && (
                  <>
                    <div style={{ height: 1, background: 'var(--theme-border, #e5e7eb)', margin: '4px 0' }} />
                    <button
                      onClick={() => {
                        setActiveChannelId(null);
                        setMyChannelOverride(null);
                        setShowSettings(true);
                        setShowChannelPicker(false);
                      }}
                      style={{
                        width: '100%', textAlign: 'left',
                        padding: '9px 14px', border: 'none', cursor: 'pointer',
                        fontSize: 13, fontWeight: 500,
                        background: 'transparent',
                        color: 'var(--primary-text, #1976d2)',
                        display: 'flex', alignItems: 'center', gap: 8,
                      }}
                    >
                      <Plus size={13} aria-hidden />
                      {t('channel.new_channel_button')} ({myChannels.length})
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Close dropdown on outside click */}
            {showChannelPicker && (
              <div
                style={{ position: 'fixed', inset: 0, zIndex: 49 }}
                onClick={() => setShowChannelPicker(false)}
                aria-hidden
              />
            )}

          </div>
        )}

        {/* Back button for external channel view (browsing from directory) */}
        {!isOwnerView && onBack && !openPlaylist && (
          <div style={{
            padding: '6px 16px',
            borderBottom: '1px solid var(--theme-border, #e5e7eb)',
            background: 'var(--card-bg, var(--theme-card, #fff))',
            flexShrink: 0,
          }}>
            <button
              onClick={onBack}
              aria-label={t('channel.back_to_directory', 'Back to channel directory')}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--primary-text, #1976d2)', fontSize: 13, fontWeight: 500,
                display: 'flex', alignItems: 'center', gap: 4, padding: '2px 0',
              }}
            >
              ← {t('channel_directory', 'Channels')}
            </button>
          </div>
        )}

        {/* Open playlist view */}
        {openPlaylist && (
          <PlaylistPage
            playlist={openPlaylist}
            videos={channelVideos}
            onBack={() => setOpenPlaylist(null)}
            isOwner={isOwnerView}
            onAddVideo={isOwnerView && !readOnly ? async (videoId) => {
              await addVideo(openPlaylist, videoId);
              setOpenPlaylist((pl) => pl ? { ...pl, videoIds: [...pl.videoIds, videoId] } : pl);
            } : undefined}
            onRemoveVideo={isOwnerView && !readOnly ? async (videoId) => {
              await removeVideo(openPlaylist, videoId);
              setOpenPlaylist((pl) => pl ? { ...pl, videoIds: pl.videoIds.filter((id) => id !== videoId) } : pl);
            } : undefined}
          />
        )}

        {/* Scrollable zone: header scrolls away, tabs bar becomes sticky */}
        {!openPlaylist && (
          <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>

            {channel && (
              <ChannelHeader
                channel={channel}
                isOwner={isOwnerView}
                myEmail={myEmail}
                onChannelUpdated={(updated) => {
                  if (isOwnerView) setMyChannelOverride(updated);
                  else setExternalChannel(updated);
                }}
              />
            )}

            {/* Sticky tabs + settings toolbar */}
            {channel && (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '0 20px',
                borderBottom: '1px solid var(--theme-border, #e5e7eb)',
                background: 'var(--card-bg, var(--theme-card, #fff))',
                position: 'sticky', top: 0, zIndex: 10,
              }}>
                {/* Tabs */}
                <div style={{ display: 'flex', gap: 2 }}>
                  {(['videos', 'playlists'] as ChannelTab[]).map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      aria-current={activeTab === tab ? 'page' : undefined}
                      style={{
                        padding: '10px 16px', border: 'none', background: 'none',
                        fontSize: 13, fontWeight: activeTab === tab ? 600 : 400,
                        color: activeTab === tab ? 'var(--theme-primary, #1976d2)' : 'var(--text-secondary, #6b7280)',
                        borderBottom: activeTab === tab ? '2px solid var(--theme-primary, #1976d2)' : '2px solid transparent',
                        cursor: 'pointer', transition: 'all 0.15s',
                      }}
                    >
                      {tab === 'videos' ? t('channel.tab_videos') : t('channel.tab_playlists')}
                    </button>
                  ))}
                </div>

                {isOwnerView && !readOnly && (
                  <button
                    onClick={() => setShowSettings((v) => !v)}
                    aria-label={t('channel.settings_aria')}
                    aria-pressed={showSettings}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 5,
                      padding: '6px 12px', borderRadius: 6, cursor: 'pointer',
                      fontSize: 12, fontWeight: 500,
                      background: showSettings ? 'color-mix(in srgb, var(--theme-primary, #1976d2) 8%, transparent)' : 'var(--surface-hover, #f3f4f6)',
                      color: showSettings ? 'var(--theme-primary, #1976d2)' : 'var(--text-primary, #111)',
                      border: showSettings ? '1px solid color-mix(in srgb, var(--theme-primary, #1976d2) 27%, transparent)' : '1px solid var(--theme-border, #e5e7eb)',
                      transition: 'all 0.15s',
                    }}
                  >
                    <Settings size={13} aria-hidden />
                    {showSettings ? t('channel.settings_close') : t('channel.settings_open')}
                  </button>
                )}
              </div>
            )}

            {/* Tab content */}
            <div style={{ padding: '20px' }}>
            {activeTab === 'videos' && (
              videosLoading ? (
                <div style={{ color: 'var(--text-secondary, #6b7280)', fontSize: 13 }}>{t('channel.loading_videos')}</div>
              ) : channelVideos.length === 0 ? (
                <NoVideos />
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
                  {channelVideos.map((video) => (
                    <VideoCard
                      key={video.id}
                      video={video}
                      onEdit={isOwnerView ? onEdit : undefined}
                      myChannels={isOwnerView ? myChannels : undefined}
                      onChannelAssigned={isOwnerView ? (videoId, channelId) => {
                        setChannelVideos((prev) =>
                          channelId === null
                            ? prev.filter((v) => v.id !== videoId)
                            : prev.map((v) => v.id === videoId ? { ...v, channelId: channelId } : v)
                        );
                      } : undefined}
                    />
                  ))}
                </div>
              )
            )}

            {activeTab === 'playlists' && (
              <div>
                {isOwnerView && channel && !readOnly && (
                  <div style={{ marginBottom: 16 }}>
                    <button
                      onClick={() => setShowNewPlaylist(true)}
                      style={{
                        padding: '7px 16px', borderRadius: 6, border: 'none',
                        background: 'var(--theme-primary, #1976d2)', color: 'var(--button-primary-foreground, #fff)',
                        fontSize: 13, fontWeight: 500, cursor: 'pointer',
                      }}
                    >
                      {t('channel.new_playlist')}
                    </button>
                  </div>
                )}
                {playlists.length === 0 ? (
                  <div style={{ color: 'var(--text-secondary, #6b7280)', fontSize: 13 }}>
                    {isOwnerView ? t('channel.no_playlists_owner') : t('channel.no_playlists_public')}
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
                    {playlists.map((pl) => (
                      <PlaylistCard
                        key={pl.id}
                        // Count only what the playlist shows: the channel's videos (not binned ones).
                        playlist={videosLoading ? pl : { ...pl, videoIds: pl.videoIds.filter((id) => channelVideos.some((v) => v.id === id)) }}
                        isOwner={isOwnerView}
                        onClick={() => setOpenPlaylist(pl)}
                        onDelete={isOwnerView && !readOnly ? (p) => setPlaylistAction({ kind: 'delete', playlist: p }) : undefined}
                        onToggleVisibility={isOwnerView && !readOnly ? (p) => setPlaylistAction({ kind: 'visibility', playlist: p }) : undefined}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        )}
      </div>

      {playlistAction?.kind === 'delete' && (
        <ConfirmDialog
          danger
          title={t('playlist.delete_confirm_title', 'Delete playlist?')}
          message={t('playlist.delete_confirm_body', {
            title: playlistAction.playlist.title,
            defaultValue: '"{{title}}" will be deleted. The videos stay in the channel. This cannot be undone.',
          })}
          confirmLabel={t('playlist.delete_confirm', 'Delete')}
          onCancel={() => setPlaylistAction(null)}
          onConfirm={async () => {
            await deletePlaylist(playlistAction.playlist);
            setPlaylistAction(null);
          }}
        />
      )}

      {playlistAction?.kind === 'visibility' && (
        <ConfirmDialog
          title={playlistAction.playlist.isPublic
            ? t('playlist.private_confirm_title', 'Make playlist private?')
            : t('playlist.public_confirm_title', 'Make playlist public?')}
          message={playlistAction.playlist.isPublic
            ? t('playlist.private_confirm_body', {
                title: playlistAction.playlist.title,
                defaultValue: '"{{title}}" will be hidden from everyone except you.',
              })
            : t('playlist.public_confirm_body', {
                title: playlistAction.playlist.title,
                defaultValue: '"{{title}}" will be visible to everyone who can see this channel.',
              })}
          confirmLabel={playlistAction.playlist.isPublic
            ? t('playlist.make_private_short', 'Make private')
            : t('playlist.make_public_short', 'Make public')}
          onCancel={() => setPlaylistAction(null)}
          onConfirm={async () => {
            await setVisibility(playlistAction.playlist, !playlistAction.playlist.isPublic);
            setPlaylistAction(null);
          }}
        />
      )}

      {showNewPlaylist && channel && (
        <TextInputDialog
          title={t('playlist.new_title', 'New playlist')}
          label={t('playlist.name_label', 'Playlist name')}
          placeholder={t('playlist.name_placeholder', 'e.g. Week 1 lessons')}
          confirmLabel={t('playlist.create', 'Create')}
          onCancel={() => setShowNewPlaylist(false)}
          onConfirm={async (name) => {
            const created = await createPlaylist(channel.id, name);
            if (created) {
              toast.success(t('playlist.created', 'Playlist created'));
              setShowNewPlaylist(false);
            } else {
              toast.error(t('playlist.create_error', 'Could not create the playlist.'));
            }
          }}
        />
      )}

      {/* Settings panel — slide-in panel on the right */}
      {isOwnerView && showSettings && !readOnly && (
        <div
          style={{
            width: 300,
            flexShrink: 0,
            borderLeft: '1px solid var(--theme-border, #e5e7eb)',
            background: 'var(--card-bg, var(--theme-card, #fff))',
            overflowY: 'auto',
            transition: 'width 0.2s ease',
          }}
        >
          <ChannelSettingsPanel
            onClose={() => setShowSettings(false)}
            onChannelReady={(updated) => {
              // Update channel in place — no refresh() to avoid loading-flash loop
              setMyChannelOverride(updated);
              // After first channel creation, close settings to reveal the channel header
              if (!resolvedMyChannel) setShowSettings(false);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default ChannelPage;
